import { useEffect, useRef, useState } from "react";
import { Button, cn } from "@carry/ui";
import { BottomBar } from "../components/BottomBar";
import { Header } from "../components/Header";
import type { DriverFetcher } from "../hooks";
import { listDriverStopProof, uploadDriverProof } from "../hooks";
import type { DriverStop } from "../lib/normalize";

/**
 * F14 Slice 4: 配達証跡画像 upload 画面.
 * JPEG/PNG、10MB 以内、signature/photo の 2 種類。
 * 既存画像は presigned URL でサムネ表示。
 */
type Proof = {
  id: string;
  image_type: string;
  image_url: string;
  created_at: string;
};

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const ACCEPTED_MIME = ["image/jpeg", "image/png"];

export function ProofUpload({
  stop,
  fetcher,
  onBack,
}: {
  stop: DriverStop;
  fetcher: DriverFetcher;
  onBack: () => void;
}) {
  const [imageType, setImageType] = useState<"signature" | "photo">("photo");
  const [acting, setActing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [proofs, setProofs] = useState<Proof[] | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    void listDriverStopProof(fetcher, stop.id)
      .then((data) => {
        if (!cancelled) setProofs(data);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [fetcher, stop.id]);

  async function handleFile(file: File) {
    setError(null);
    if (!ACCEPTED_MIME.includes(file.type)) {
      setError("JPEG または PNG のみ対応");
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setError("ファイルサイズは 10MB までです");
      return;
    }
    setActing(true);
    try {
      const uploaded = await uploadDriverProof(fetcher, stop.id, file, imageType);
      setProofs((prev) => [...(prev ?? []), uploaded as Proof]);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setActing(false);
    }
  }

  return (
    <div className="min-h-screen bg-[var(--bg)] pb-[var(--bottom-bar-clearance)]">
      <Header title="証跡画像" sub={stop.name} showBack onBack={onBack} />
      <div className="px-3 py-3.5">
        <div className="mb-3 rounded-xl bg-[var(--bg-raised)] p-3.5 shadow-sm">
          <div className="mb-2.5 text-[length:var(--fs-sm)] font-semibold">種別</div>
          <div className="flex gap-2">
            {(["photo", "signature"] as const).map((t) => {
              const sel = imageType === t;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setImageType(t)}
                  className={cn(
                    "flex-1 rounded-lg border px-3 py-2 text-[length:var(--fs-sm)] font-medium",
                    sel
                      ? "border-[var(--ok)] bg-[var(--st-done-bg)] text-[var(--ok)]"
                      : "border-[var(--rule-soft)] bg-[var(--bg-sunken)] text-[var(--ink-2)]",
                  )}
                >
                  {t === "photo" ? "写真" : "署名"}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mb-3 rounded-xl bg-[var(--bg-raised)] p-3.5 shadow-sm">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png"
            capture="environment"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleFile(f);
              e.target.value = ""; // 同じファイルの再選択を許可
            }}
            className="hidden"
          />
          <Button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={acting}
            className="w-full"
          >
            {acting ? "アップロード中…" : imageType === "photo" ? "写真を撮影" : "署名を撮影"}
          </Button>
          <div className="mt-2 text-[length:var(--fs-xs)] text-[var(--ink-3)]">
            JPEG または PNG、10MB まで
          </div>
        </div>

        {error && (
          <div className="mb-3 rounded-lg bg-[var(--st-cancel-bg)] px-3 py-2 text-[length:var(--fs-xs)] text-[var(--st-cancel-ink)]">
            ⚠ {error}
          </div>
        )}

        <div className="mb-3 rounded-xl bg-[var(--bg-raised)] p-3.5 shadow-sm">
          <div className="mb-2.5 text-[length:var(--fs-sm)] font-semibold">既存の証跡</div>
          {proofs === null && (
            <div className="text-[length:var(--fs-xs)] text-[var(--ink-3)]">読み込み中…</div>
          )}
          {proofs !== null && proofs.length === 0 && (
            <div className="text-[length:var(--fs-xs)] text-[var(--ink-3)]">まだありません</div>
          )}
          {proofs && proofs.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {proofs.map((p) => (
                <a key={p.id} href={p.image_url} target="_blank" rel="noopener noreferrer">
                  <img
                    src={p.image_url}
                    alt={p.image_type}
                    className="aspect-square w-full rounded border border-[var(--rule-soft)] object-cover"
                  />
                  <div className="mt-0.5 text-[length:var(--fs-xs)] text-[var(--ink-3)]">
                    {p.image_type === "signature" ? "署名" : "写真"}
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>

      <BottomBar>
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          className="h-auto min-h-[var(--row-h)] flex-1 px-4 py-3 text-[length:var(--fs-sm)]"
        >
          戻る
        </Button>
      </BottomBar>
    </div>
  );
}
